import { useCallback, useEffect, useRef, useState } from "react";
import { hasSeenFeatureTour, markFeatureTourSeen } from "../utils/featureTours";

/**
 * 编排「首次功能引导气泡」的出现时机：
 * - 当 enabled 满足、且该引导从未被看过时，延时展示一次；
 * - 关闭后写入「已看过」标记，之后不再出现。
 *
 * 展示前/关闭后的副作用（例如展开侧边栏、再恢复侧边栏）由调用方通过
 * onBeforeShow / onAfterClose 提供，本钩子只负责时机与去重，保持通用。
 *
 * @param {Object}   params
 * @param {boolean}  params.enabled       是否满足前置条件（如已登录、有权限）
 * @param {string}   params.tourId        引导标识，用于记忆「已看过」
 * @param {Function} [params.onBeforeShow] 展示前回调（如展开侧边栏让锚点可见）
 * @param {Function} [params.onAfterClose] 关闭后回调（如恢复侧边栏原状）
 * @param {number}   [params.delay=500]   满足条件到真正弹出的延时（毫秒），
 *                                         给展开动画/DOM 渲染留出时间，保证箭头指准
 * @returns {{ open: boolean, dismiss: () => void }}
 */
export function useFeatureAnnouncementTour({ enabled, tourId, onBeforeShow, onAfterClose, delay = 500 }) {
    const [open, setOpen] = useState(false);

    // 以「最新引用」方式保存回调，使触发逻辑无需把回调放进依赖、避免重复触发
    const callbacksRef = useRef({ onBeforeShow, onAfterClose });
    callbacksRef.current = { onBeforeShow, onAfterClose };

    useEffect(() => {
        if (!enabled || !tourId) {
            return undefined;
        }

        if (hasSeenFeatureTour(tourId)) {
            return undefined;
        }

        callbacksRef.current.onBeforeShow?.();
        const timer = setTimeout(() => setOpen(true), delay);

        return () => clearTimeout(timer);
    }, [enabled, tourId, delay]);

    const dismiss = useCallback(() => {
        markFeatureTourSeen(tourId);
        setOpen(false);
        callbacksRef.current.onAfterClose?.();
    }, [tourId]);

    return { open, dismiss };
}
